import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Flex,
  Box,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Button,
  Heading,
  Text,
  Image,
  Switch,
  useColorModeValue,
  FormErrorMessage,
  Center,
  useToast,
} from "@chakra-ui/react";

import { appRequest } from "routes";
import { BASE_PATH } from "utils/env";

type LoginFormValues = {
  email: string;
  password: string;
};

type LoginFormErrors = {
  email?: string;
  password?: string;
};

export default function Login() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const Navigate = useNavigate();

  const [values, setValues] = useState<LoginFormValues>({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [rememberMe, setRememberMe] = useState(false);

  const validateEmail = () => {
    if (!values.email) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Email is required",
      }));
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Invalid email format",
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: undefined,
      }));
    }
  };

  const validatePassword = () => {
    if (!values.password) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password: "Password is required",
      }));
    } else if (values.password.length < 6) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password: "Password must be at least 6 characters long",
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password: undefined,
      }));
    }
  };

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    validateEmail();
    validatePassword();

    if (!errors.email && !errors.password) {
      const input = {
        username: values.email,
        password: values.password,
      };

      try {
        setLoading(true);

        const response: any = await appRequest(
          "auth",
          "login",
          input
        );

        if (response) {
          const { token } = response;

          if (!token) {
            toast({
              title: response.message,
              status: "error",
              duration: 5000,
              isClosable: true,
              position: "top-right",
            });

            return;
          }

          if (rememberMe) {
            localStorage.setItem(
              "rememberedEmail",
              values.email
            );
            localStorage.setItem(
              "rememberedPassword",
              values.password
            );
          } else {
            localStorage.removeItem("rememberedEmail");
            localStorage.removeItem("rememberedPassword");
          }

          localStorage.setItem("auth-token", token);

          window.location.href = BASE_PATH;
        }
      } catch (error: any) {
        toast({
          title: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-left",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;

    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  useEffect(() => {
    const rememberedEmail =
      localStorage.getItem("rememberedEmail");

    const rememberedPassword =
      localStorage.getItem("rememberedPassword");

    if (rememberedEmail && rememberedPassword) {
      setValues({
        email: rememberedEmail,
        password: rememberedPassword,
      });

      setRememberMe(true);
    }
  }, []);

  return (
    <Flex
      minH={"100vh"}
      align={"center"}
      justify={"center"}
      bg={useColorModeValue("gray.50", "gray.800")}
    >
      <Stack
        spacing={2}
        mx={"auto"}
        w={"full"}
        maxW={"lg"}
        py={12}
        px={6}
      >
        <Stack align={"center"}>
          <Image
            src={BASE_PATH + "/img/sequelstring.webp"}
            width={320}
          />
        </Stack>

        <form onSubmit={handleSubmit}>
          <Box
            rounded={"lg"}
            bg={useColorModeValue("white", "gray.700")}
            boxShadow={"md"}
            p={8}
          >
            <Heading
              color="black"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="700"
              mb="11px"
            >
              Welcome Back!
            </Heading>

            <Text
              fontSize={{ base: "sm", sm: "md" }}
              color={useColorModeValue(
                "gray.400",
                "gray.800"
              )}
              fontWeight="700"
              mb={6}
            >
              Enter your email and password to sign in
            </Text>

            <Stack spacing={4}>
              <FormControl
                id="email"
                isInvalid={!!errors.email}
              >
                <FormLabel color="gray.700">
                  Email Address
                </FormLabel>

                <Input
                  type="email"
                  placeholder="Your email address"
                  name="email"
                  borderRadius="15px"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={validateEmail}
                  _hover={{
                    border: "2px solid",
                    borderColor: "blue.1000",
                  }}
                  _focus={{
                    border: "2px solid",
                    borderColor: "blue.1000",
                  }}
                />

                <FormErrorMessage>
                  {errors.email}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                id="password"
                isInvalid={!!errors.password}
              >
                <FormLabel color="gray.700">
                  Password
                </FormLabel>

                <Input
                  type="password"
                  placeholder="Your password"
                  name="password"
                  borderRadius="15px"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={validatePassword}
                  _hover={{
                    border: "2px solid",
                    borderColor: "blue.1000",
                  }}
                  _focus={{
                    border: "2px solid",
                    borderColor: "blue.1000",
                  }}
                />

                <FormErrorMessage>
                  {errors.password}
                </FormErrorMessage>
              </FormControl>

              <Stack spacing={5}>
                <Stack
                  mb={3}
                  direction={{
                    base: "column",
                    sm: "row",
                  }}
                  align={"start"}
                  justify={"space-between"}
                >
                  <Switch
                    colorScheme="teal"
                    size="md"
                    cursor="pointer"
                    isChecked={rememberMe}
                    onChange={() =>
                      setRememberMe(!rememberMe)
                    }
                  >
                    <Text as="span" color="gray.700">
                      Remember me
                    </Text>
                  </Switch>
                </Stack>

                <Button
                  type="submit"
                  bg="teal.300"
                  h={"45px"}
                  fontWeight="700"
                  color="white"
                  borderRadius="15px"
                  _hover={{
                    bg: "teal.400",
                  }}
                  isLoading={loading}
                >
                  SIGN IN
                </Button>

                <Center>
                  <Text
                    color="gray.700"
                    fontSize={{ base: "sm", sm: "md" }}
                  >
                    Can’t access?{" "}
                    <Text
                      as="span"
                      fontWeight="700"
                      color="teal.300"
                      cursor="pointer"
                      _hover={{
                        borderBottom: "2px solid",
                      }}
                      onClick={() =>
                        Navigate("/forgot-password")
                      }
                    >
                      Click here
                    </Text>
                  </Text>
                </Center>
              </Stack>
            </Stack>
          </Box>
        </form>
      </Stack>
    </Flex>
  );
}